import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  SlideshowSelectEvent,
} from '@theme/events';

/**
 * True when Size (etc.) was changed — page must not jump to the gallery (TWS-984).
 * Prefers the flag captured at click time on variant-picker.
 * Size uses the same morph / video / first-tile path as Colour/Shape — only scroll differs.
 *
 * @param {VariantUpdateEvent} event
 * @param {ParentNode} sectionHtml
 * @param {Element} galleryEl
 * @returns {boolean}
 */
function optionChangeSkipsGalleryScroll(event, sectionHtml, galleryEl) {
  // Primary: set on the clicked option in variant-picker before fetch/morph.
  if (event.detail?.data?.updatesGallery === false) return true;
  if (event.detail?.data?.updatesGallery === true) return false;

  const optionValueId = event.detail?.sourceId;
  if (!optionValueId) return false;

  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(String(optionValueId))
      : String(optionValueId).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

  // Search whole document — gallery + picker can sit in nested blocks of one section.
  const optionEl =
    document.querySelector(`variant-picker [data-option-value-id="${escaped}"]`) ||
    galleryEl.closest('.shopify-section, dialog')?.querySelector(`[data-option-value-id="${escaped}"]`) ||
    sectionHtml.querySelector(`[data-option-value-id="${escaped}"]`);

  if (!(optionEl instanceof Element)) return false;

  const group = optionEl.closest('[data-updates-gallery], fieldset.variant-option, .variant-option--dropdowns');
  if (!(group instanceof Element)) return false;

  if (group.getAttribute('data-updates-gallery') === 'false') return true;
  if (group.getAttribute('data-updates-gallery') === 'true') return false;

  const optionName = (group.getAttribute('data-option-name') || '').toLowerCase();
  if (optionName === 'size' || optionName.endsWith('-size')) return true;

  const labelText = (group.querySelector('.variant-option__label')?.textContent || '').toLowerCase();
  return /selected\s+size\s*:/.test(labelText);
}

/**
 * Restore scroll if layout/morph nudges the page (TWS-984 size only).
 * @param {number} scrollY
 */
function preserveScrollPosition(scrollY) {
  requestAnimationFrame(() => {
    if (Math.abs(window.scrollY - scrollY) > 1) {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }
  });
}

/**
 * Section fetches sometimes omit PDP badge markup (same as PLP card-gallery morph).
 * Keep the live badge when the fetched gallery would drop or empty it.
 *
 * @param {Element} liveGallery
 * @param {Element} fetchedGallery
 */
function preserveMediaGalleryBadges(liveGallery, fetchedGallery) {
  const currentBadges = liveGallery.querySelector('.product-media__badge-container');
  const newBadges = fetchedGallery.querySelector('.product-media__badge-container');
  const currentHasTags = Boolean(currentBadges?.querySelector('.product-card__tag')?.textContent?.trim());

  if (!currentHasTags) return;

  if (!newBadges) {
    const clone = /** @type {Element} */ (currentBadges.cloneNode(true));
    const insertBefore =
      fetchedGallery.querySelector('[data-sirv-pdp-gallery], .sirv-pdp-gallery, slideshow-component') ||
      fetchedGallery.firstChild;
    if (insertBefore) {
      fetchedGallery.insertBefore(clone, insertBefore);
    } else {
      fetchedGallery.prepend(clone);
    }
    return;
  }

  const newHasTags = Boolean(newBadges.querySelector('.product-card__tag')?.textContent?.trim());
  if (!newHasTags) {
    newBadges.replaceWith(/** @type {Element} */ (currentBadges.cloneNode(true)));
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    // Badge Manager / theme badges must sit on <media-gallery>, not inside slideshow-slide,
    // or they swipe away with the peek carousel. With the theme setting
    // perf_pdp_badge_hosts_in_place on (data-badge-hosts-in-place, PDP-BADGE), Badge Manager hosts
    // are left where the app places them (one owner per element, so the two scripts never move the
    // same node back and forth) and the theme keeps them still while swiping with CSS only
    // (assets/product-gallery-accent.css); only the theme's own badge container moves.
    this.#pinOverlayBadges();
    this.#badgePinObserver = new MutationObserver(() => this.#pinOverlayBadges());
    this.#badgePinObserver.observe(this, { childList: true, subtree: true });
    signal.addEventListener('abort', () => this.#badgePinObserver?.disconnect());

    // TWS-1029: force first tile on load (mobile can otherwise land on video via autoplay+focus),
    // then desktop-only video autoplay after paint.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#ensureInitialGalleryPosition();
        requestAnimationFrame(() => {
          this.#initialGallerySettled = true;
        });
      });
    });
  }

  /** @type {MutationObserver | undefined} */
  #badgePinObserver;

  /**
   * Lift overlay badges out of the scrolling slide track onto the gallery root. With
   * data-badge-hosts-in-place (PDP-BADGE), only the theme's own badge container: Badge Manager
   * hosts ([data-badge-manager-host], [data-badge-plp-host]) are never moved here, because the app
   * re-places a host whenever it leaves the media element it chose, so moving it starts a
   * move-and-restore cycle that runs every frame.
   */
  #pinOverlayBadges() {
    const hosts = this.querySelectorAll(
      this.hasAttribute('data-badge-hosts-in-place')
        ? '.product-media__badge-container'
        : '.badge-manager-host--overlay, .badge-manager-host--pdp, .badge-plp-host--overlay, .product-media__badge-container'
    );

    for (const host of hosts) {
      if (!(host instanceof HTMLElement)) continue;
      if (!host.closest('slideshow-slides, slideshow-slide, .sirv-pdp-gallery__viewer')) continue;
      if (host.parentElement === this) continue;

      const insertBefore =
        this.querySelector(
          ':scope > slideshow-component, :scope > [data-sirv-pdp-gallery], :scope > .sirv-pdp-gallery, :scope > .media-gallery__images-grid-wrapper'
        ) || this.firstChild;
      this.insertBefore(host, insertBefore);
    }
  }

  /** @type {boolean} False until initial slide-0 settle so layout scroll is not treated as user swipe. */
  #initialGallerySettled = false;

  /** @type {WeakMap<Element, number>} */
  #videoAutoplayTimers = new WeakMap();

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update: same morph + first-tile + video restore for every option.
   * TWS-984: Size skips only `#scrollPageToGallery` (and freezes scrollY after morph).
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Same gallery update as Colour/Shape — only Size skips page scroll (TWS-984).
    const skipScroll = optionChangeSkipsGalleryScroll(event, source, this);
    const scrollYBeforeMorph = skipScroll ? window.scrollY : null;

    // Allow Sirv remount on every option change (parity with non-size swatches).
    delete this.dataset.galleryMediaUnchanged;

    // Pure section snapshot (pre-morph). Do not inject live 360 via carry-forward —
    // that kept the previous variant's video when section had empty variant_images
    // (and previously omitted 360 from markup).
    /** @type {Element} */
    const gallerySnapshot = /** @type {Element} */ (newMediaGallery.cloneNode(true));

    this.#teardownMediaBeforeVariantMorph();

    this.#initialGallerySettled = false;

    preserveMediaGalleryBadges(this, newMediaGallery);

    morph(this, newMediaGallery);

    this.#replaceProductVideoSubtrees(gallerySnapshot);
    this.#restoreVideo360TilesFromSnapshot(gallerySnapshot);

    // Double rAF: wait for morph layout/scroll-snap before forcing slide 0.
    // Otherwise mobile can re-snap onto the video (2nd media) while new images load.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#resetGalleryAfterVariantUpdate();
        this.#healAndPlayVideo360Tiles();
        if (!skipScroll) {
          this.#scrollPageToGallery();
        } else if (scrollYBeforeMorph != null) {
          if (Math.abs(window.scrollY - scrollYBeforeMorph) > 1) {
            window.scrollTo({ top: scrollYBeforeMorph, behavior: 'instant' });
          }
          preserveScrollPosition(scrollYBeforeMorph);
        }
        requestAnimationFrame(() => {
          this.#initialGallerySettled = true;
        });
      });
    });
  };

  /**
   * Scroll the page so the gallery is visible after a media-changing variant update.
   * Uses the sticky header offset so content is not hidden under the header.
   */
  #scrollPageToGallery() {
    const rawOffset = getComputedStyle(document.documentElement)
      .getPropertyValue('--sticky-header-offset')
      .trim();
    const stickyOffset = Number.parseFloat(rawOffset) || 0;
    const top = this.getBoundingClientRect().top + window.scrollY - stickyOffset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'instant',
    });
  }

  /**
   * Show the first media item (product image) and pause off-slide video.
   * Used on initial load (TWS-1029) and after variant morph (TWS-983).
   * Desktop autoplay is applied separately via #syncActiveSlideVideo / #deferGridVideoSync.
   */
  #forceFirstSlideAndPauseOthers() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const allSlides = slideshow.refs?.slides;
    if (!allSlides?.length) return;

    const scroller = slideshow.refs?.scroller ?? slideshow.querySelector('slideshow-slides');
    if (scroller instanceof HTMLElement) {
      scroller.scrollLeft = 0;
    }

    slideshow.select(0, undefined, { animate: false });

    const activeSlide = allSlides[0];
    for (const slide of allSlides) {
      if (slide === activeSlide) continue;
      this.#pauseMediaInSlide(slide);
    }
  }

  /**
   * Page load: stay on first image; mobile must not autoplay/load the video tile.
   * Desktop may autoplay after settle when Product media setting is on.
   */
  #ensureInitialGalleryPosition() {
    this.#forceFirstSlideAndPauseOthers();

    if (!window.matchMedia('(min-width: 1024px)').matches) {
      // Mobile carousel/grid-mobile: never load videos until user opens that tile.
      this.#pauseAllGalleryVideos();
      return;
    }

    this.#selectInitialAutoplayVideo();
    this.#syncActiveSlideVideo();
    this.#deferGridVideoSync();
  }

  /**
    * After a variant morph, always show the first media item (static product image).
   * Mobile carousels can otherwise land on the video slide (scroll-snap race).
   * Desktop grid autoplay behaviour is preserved when the setting is on.
   */
  #resetGalleryAfterVariantUpdate() {
    this.#forceFirstSlideAndPauseOthers();

    if (!window.matchMedia('(min-width: 1024px)').matches) {
      this.#pauseAllGalleryVideos();
      return;
    }

    this.#selectInitialAutoplayVideo();
    this.#syncActiveSlideVideo();
    this.#deferGridVideoSync();
  }

  /**
   * Make carousel autoplay visible by selecting one product video on desktop.
   * Grid galleries keep their initial image and are handled by #syncGridVideos.
   */
  #selectInitialAutoplayVideo() {
    if (this.classList.contains('media-gallery--images-grid')) return;
    if (!this.#shouldAutoplayVideo()) return;

    let attempts = 0;
    const selectVideo = () => {
      const slideshow = this.slideshow;
      const videoIndex = slideshow?.slides?.findIndex((slide) =>
        slide instanceof Element &&
        (slide.classList.contains('product-media-container--video') ||
          slide.classList.contains('product-media-container--external_video') ||
          slide.classList.contains('product-media-container--video-360'))
      );

      if (videoIndex > 0 && slideshow?.refs?.scroller) {
        const selectedVideo = slideshow.slides[videoIndex];
        Promise.resolve(slideshow.select(videoIndex, undefined, { animate: false })).then(() => {
          if (slideshow.current === videoIndex && selectedVideo instanceof Element) {
            this.#playVideosInContainer(selectedVideo);
          }
        });
        return;
      }

      if (attempts < 10) {
        attempts += 1;
        requestAnimationFrame(selectVideo);
      }
    };

    selectVideo();
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * When the visible slide changes, pause video/model playback in other slides so audio
   * and CPU work do not continue (e.g. metafield 360° embeds outside deferred-media).
   * Mobile (TWS-1029): only start video after a user-initiated select (swipe/thumb),
   * never from layout scroll while settling on the product image.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const activeSlide = event.detail?.slide;
    if (!(activeSlide instanceof Element)) return;

    const slides = this.slideshow?.slides;
    if (!slides?.length) return;

    for (const slide of slides) {
      if (slide === activeSlide) continue;
      this.#pauseMediaInSlide(slide);
    }

    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const userInitiated = event.detail?.userInitiated === true;

    if (isDesktop) {
      this.#playMediaInSlide(activeSlide);
      return;
    }

    // Mobile: play only when the user navigated to the video tile after initial settle.
    if (this.#initialGallerySettled && userInitiated) {
      this.#playMediaInSlide(activeSlide, { force: true });
    }
  };

  /**
   * A 360 video can be configured once for the whole product while a variant
   * section response omits it. Carry the existing tile into that response so
   * changing image swatches cannot remove the product-level video.
   *
   * @param {Element} newRoot
   */
  #carryForwardVideo360(newRoot) {
    const videoSelector =
      '.images-grid-item--video-360, slideshow-slide.product-media-container--video-360, li.product-media-container--video-360';

    if (newRoot.querySelector(videoSelector)) return;

    const oldGridVideo = this.querySelector('.images-grid-item--video-360');
    if (oldGridVideo) {
      const grid = newRoot.querySelector('.media-gallery__images-grid-desktop');
      let targetRow = grid?.querySelector('.images-grid-row:last-child');

      if (grid && (!targetRow || targetRow.children.length >= 2)) {
        targetRow = document.createElement('div');
        targetRow.className = `images-grid-row images-grid-row--${grid.querySelectorAll('.images-grid-row').length + 1}`;
        grid.append(targetRow);
      }

      if (targetRow) {
        this.#appendResetVideoClone(oldGridVideo, targetRow);
      }
    }

    const oldSlides = [...this.querySelectorAll('slideshow-slide.product-media-container--video-360')];
    const newSlideTracks = [...newRoot.querySelectorAll('slideshow-slides')];
    oldSlides.forEach((oldSlide, index) => {
      const targetTrack = newSlideTracks[index] || newSlideTracks[0];
      if (!targetTrack) return;
      this.#appendResetVideoClone(oldSlide, targetTrack);
    });

    const oldZoomVideos = [...this.querySelectorAll('li.product-media-container--video-360')];
    const newZoomLists = [...newRoot.querySelectorAll('dialog ol, dialog ul')];
    oldZoomVideos.forEach((oldVideo, index) => {
      const targetList = newZoomLists[index] || newZoomLists[0];
      if (!targetList) return;
      this.#appendResetVideoClone(oldVideo, targetList);
    });
  }

  /**
   * @param {Element} source
   * @param {Element} target
   */
  #appendResetVideoClone(source, target) {
    const clone = source.cloneNode(true);
    this.#resetLoadedDeferredMedia(clone);
    target.append(clone);
  }

  /**
   * Stop product / 360° media before morph so loaded deferred-media is not partially
   * morphed into a blank tile (poster hidden, video node missing).
   */
  #teardownMediaBeforeVariantMorph() {
    for (const container of this.querySelectorAll(
      [
        '.product-media-container--video',
        '.product-media-container--external_video',
        '.product-media-grid-360',
        '.product-media-container--video-360',
        '.product-media--external-video-360',
      ].join(', ')
    )) {
      this.#resetLoadedDeferredMedia(container);
    }
  }

  /**
   * Replace normal product video tiles from a pre-morph section snapshot.
   * Do not use the post-morph section fragment — morph moves those nodes into the live gallery.
   *
   * @param {ParentNode} newRoot
   */
  #replaceProductVideoSubtrees(newRoot) {
    if (!(newRoot instanceof Element)) return;

    this.#replaceMatchedNodes(
      newRoot,
      // Exclude metafield 360 tiles (handled by #restoreVideo360TilesFromSnapshot)
      '.product-media-container--video:not(.product-media-container--video-360), .product-media-container--external_video:not(.product-media-container--video-360)'
    );
  }

  /**
   * Re-inject custom.video_360_url / product-media-grid-360 tiles after variant morph.
   * Morph can empty / move the section fragment; #replaceMatchedNodes then removes live 360
   * tiles (newNodes empty) and leaves a blank 2nd grid cell until hard refresh.
   *
   * @param {Element} snapshotRoot - Cloned media-gallery from the section response (pre-morph).
   */
  #restoreVideo360TilesFromSnapshot(snapshotRoot) {
    if (!(snapshotRoot instanceof Element)) return;

    this.#syncGridVideo360Tiles(snapshotRoot);

    // Carousel + zoom dialog 360 (not grid)
    this.#replaceMatchedNodes(
      snapshotRoot,
      '.product-media-container--video-360:not(.product-media-container--grid-360)'
    );
    this.#replaceMatchedNodes(snapshotRoot, 'li.product-media-container--video-360[id^="product-video-360-"]');
  }

  /**
   * Place each snapshot `.images-grid-item--video-360` into the live grid at the same
   * row/child index (desktop + mobile image grids).
   * Prefer URL match when layout indices shift (images-only ↔ 360-only variants).
   *
   * @param {Element} snapshotRoot
   */
  #syncGridVideo360Tiles(snapshotRoot) {
    const gridSelectors = ['.media-gallery__images-grid-desktop', '.media-gallery__images-grid-mobile'];

    for (const gridSel of gridSelectors) {
      const liveGrid = this.querySelector(gridSel);
      const srcGrid = snapshotRoot.querySelector(gridSel);
      if (!(liveGrid instanceof Element) || !(srcGrid instanceof Element)) continue;

      const src360Items = [...srcGrid.querySelectorAll('.images-grid-item--video-360')];
      const srcRows = [...srcGrid.querySelectorAll(':scope > .images-grid-row')];
      const liveRows = [...liveGrid.querySelectorAll(':scope > .images-grid-row')];

      if (!src360Items.length) {
        liveGrid.querySelectorAll('.images-grid-item--video-360').forEach((el) => el.remove());
        continue;
      }

      // Drop old 360 tiles first when counts differ so we never leave a stale video URL
      // (common when one size has image tiles and another only has metafield 360).
      if (src360Items.length !== liveGrid.querySelectorAll('.images-grid-item--video-360').length) {
        liveGrid.querySelectorAll('.images-grid-item--video-360').forEach((el) => {
          this.#resetLoadedDeferredMedia(el);
          el.remove();
        });
      }

      for (const src360 of src360Items) {
        const srcRow = src360.closest('.images-grid-row');
        if (!(srcRow instanceof Element)) continue;

        const rowIdx = srcRows.indexOf(srcRow);
        const itemIdx = [...srcRow.children].indexOf(src360);
        let liveRow = liveRows[rowIdx];

        // Ensure a row exists when variant has 360 only and morph left empty / mismatched grid.
        if (!(liveRow instanceof Element) && liveGrid) {
          liveRow = document.createElement('div');
          liveRow.className = `images-grid-row images-grid-row--${liveRows.length + 1}`;
          liveGrid.append(liveRow);
          liveRows.push(liveRow);
        }
        if (!(liveRow instanceof Element) || itemIdx < 0) continue;

        const clone = /** @type {Element} */ (src360.cloneNode(true));
        this.#resetLoadedDeferredMedia(clone);

        const liveAt = liveRow.children[itemIdx];
        if (liveAt) {
          liveAt.replaceWith(clone);
        } else {
          liveRow.append(clone);
        }
      }
    }
  }

  /**
   * After morph/restore: clear broken deferred-media (loaded flag but no media) and
   * re-trigger desktop autoplay / poster for custom.video_360_url tiles.
   */
  #healAndPlayVideo360Tiles() {
    for (const deferred of this.querySelectorAll(
      '.product-media-grid-360 deferred-media, .product-media-container--video-360 deferred-media, .product-media--external-video-360 deferred-media'
    )) {
      if (!(deferred instanceof HTMLElement)) continue;

      const hasLiveMedia = Boolean(deferred.querySelector(':scope > video, :scope > iframe'));
      if (deferred.getAttribute('data-media-loaded') && !hasLiveMedia) {
        this.#resetLoadedDeferredMedia(deferred);
      }

      if (!hasLiveMedia) {
        deferred.removeAttribute('data-media-loaded');
        deferred.querySelector('.deferred-media__poster-button')?.classList.remove('deferred-media__playing');
        if ('isPlaying' in deferred) {
          /** @type {{ isPlaying: boolean }} */ (deferred).isPlaying = false;
        }
      }
    }

    this.#deferGridVideoSync();

    // Carousel path (non-grid) — play 360 on the active/first relevant slide when autoplay on.
    if (this.#shouldAutoplayVideo() && !this.classList.contains('media-gallery--images-grid')) {
      for (const container of this.querySelectorAll('.product-media-container--video-360')) {
        this.#playVideosInContainer(container);
      }
    }
  }

  /**
   * @param {ParentNode} newRoot
   * @param {string} selector
   */
  #replaceMatchedNodes(newRoot, selector) {
    const oldNodes = [...this.querySelectorAll(selector)];
    const newNodes = [...newRoot.querySelectorAll(selector)];

    oldNodes.forEach((oldNode, index) => {
      const newNode = newNodes[index];
      if (newNode) {
        this.#resetLoadedDeferredMedia(oldNode);
        oldNode.replaceWith(newNode.cloneNode(true));
        return;
      }

      // Keep the live node if the snapshot has no match (do not blank the tile).
      this.#resetLoadedDeferredMedia(oldNode);
    });

    // Snapshot has more matches than live — append clones after the last sibling of last old.
    for (let i = oldNodes.length; i < newNodes.length; i++) {
      const newNode = newNodes[i];
      if (!newNode) continue;
      const clone = newNode.cloneNode(true);
      if (clone instanceof Element) {
        this.#resetLoadedDeferredMedia(clone);
      }
      const anchor = oldNodes[oldNodes.length - 1];
      if (anchor?.parentNode) {
        anchor.parentNode.insertBefore(clone, anchor.nextSibling);
      }
    }
  }

  /**
   * @param {Element} container
   */
  #resetLoadedDeferredMedia(container) {
    /** @type {Element[]} */
    const deferredList =
      container.tagName === 'DEFERRED-MEDIA'
        ? [container]
        : [...container.querySelectorAll('deferred-media')];

    for (const deferred of deferredList) {
      if ('pauseMedia' in deferred && typeof deferred.pauseMedia === 'function') {
        deferred.pauseMedia();
      }

      for (const child of [...deferred.children]) {
        if (child instanceof HTMLTemplateElement) continue;
        if (child instanceof HTMLButtonElement && child.classList.contains('deferred-media__poster-button')) {
          continue;
        }

        if (child instanceof HTMLVideoElement) {
          child.pause();
          child.removeAttribute('src');
          child.load();
          child.remove();
        } else if (child instanceof HTMLIFrameElement) {
          this.#pauseIframe(child);
          child.setAttribute('src', 'about:blank');
          child.remove();
        }
      }

      deferred.removeAttribute('data-media-loaded');
      deferred.querySelector('.deferred-media__poster-button')?.classList.remove('deferred-media__playing');

      if ('isPlaying' in deferred) {
        deferred.isPlaying = false;
      }
    }

    for (const iframe of container.querySelectorAll('iframe')) {
      if (iframe.closest('deferred-media')) continue;
      this.#pauseIframe(iframe);
      iframe.setAttribute('src', 'about:blank');
    }

    for (const video of container.querySelectorAll('video')) {
      video.pause();
      if (!video.closest('deferred-media')) {
        video.removeAttribute('src');
        video.load();
      }
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #playIframe(iframe) {
    const src = iframe.getAttribute('src') || '';
    const win = iframe.contentWindow;
    if (!win) return;

    if (iframe.dataset.videoType === 'youtube' || /youtube\.com|youtu\.be/i.test(src)) {
      win.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (iframe.dataset.videoType === 'vimeo' || /vimeo\.com|player\.vimeo\.com/i.test(src)) {
      win.postMessage('{"method":"play"}', '*');
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #pauseIframe(iframe) {
    const src = iframe.getAttribute('src') || '';
    const win = iframe.contentWindow;
    if (!win) return;

    if (iframe.dataset.videoType === 'youtube' || /youtube\.com|youtu\.be/i.test(src)) {
      win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    } else if (iframe.dataset.videoType === 'vimeo' || /vimeo\.com|player\.vimeo\.com/i.test(src)) {
      win.postMessage('{"method":"pause"}', '*');
    }
  }

  #syncActiveSlideVideo() {
    const slideshow = this.slideshow;
    if (!slideshow) return;

    const index = slideshow.current ?? 0;
    const slide = slideshow.slides?.[index];
    if (slide instanceof Element) {
      this.#playMediaInSlide(slide);
    }
  }

  /**
   * Desktop-only autoplay gate (Product media → Autoplay videos on desktop).
   * Mobile never autoplays from this helper (TWS-1029).
   * With data-video-after-tap (theme setting perf_img_video_after_tap, E4-FALLBACK) autoplay also
   * waits for the shopper's first tap, click or key press on the page, so no video file is requested
   * before it; the poster shows until then, as today.
   *
   * @returns {boolean}
   */
  #shouldAutoplayVideo() {
    if (!window.matchMedia('(min-width: 1024px)').matches) return false;
    if (this.dataset.videoAutoplayDesktop !== 'true') return false;
    if (this.dataset.videoAfterTap === 'true' && !this.#firstTapSeen) {
      this.#autoplayAfterFirstTap();
      return false;
    }
    return true;
  }

  /**
   * True once the shopper has tapped, clicked or pressed a key on the page (E4-FALLBACK), or once
   * E4-TIMER has started the autoplay (first interaction or timer).
   */
  #firstTapSeen = false;

  /** True while the first tap listeners are attached. */
  #firstTapListening = false;

  /**
   * Waits for the first tap, click or key press anywhere on the page (E4-FALLBACK), then starts the
   * autoplay that page load skipped: the active slide's video and the desktop grid's videos. A
   * carousel is not moved to its video tile here, so the slide under the shopper's tap stays put.
   * With data-video-after-tap-seconds above 0 (E4-TIMER) the wait ends at the first interaction of any
   * kind or at the timer instead (#autoplayAfterInteractionOrTimer); without it, the code below runs
   * as before.
   */
  #autoplayAfterFirstTap() {
    if (this.#firstTapListening) return;
    this.#firstTapListening = true;

    const timerSeconds = Number.parseFloat(this.dataset.videoAfterTapSeconds ?? '');
    if (timerSeconds > 0) {
      this.#autoplayAfterInteractionOrTimer(timerSeconds);
      return;
    }

    const listenerOptions = { capture: true, passive: true, signal: this.#controller.signal };
    const onFirstTap = () => {
      window.removeEventListener('pointerdown', onFirstTap, listenerOptions);
      window.removeEventListener('keydown', onFirstTap, listenerOptions);
      this.#firstTapListening = false;
      this.#firstTapSeen = true;
      this.#syncActiveSlideVideo();
      this.#deferGridVideoSync();
    };
    window.addEventListener('pointerdown', onFirstTap, listenerOptions);
    window.addEventListener('keydown', onFirstTap, listenerOptions);
  }

  /**
   * E4-TIMER (theme setting perf_img_video_after_tap_seconds; the block prints
   * data-video-after-tap-seconds only when it is above 0). Starts the autoplay that E4-FALLBACK holds
   * at the shopper's first interaction of any kind, or once `seconds` have passed since navigation
   * start, whichever comes first. Interactions: a tap, click, key press, mouse wheel or touch, a scroll
   * of the page itself (a slider that scrolls by itself does not count), and, where the interaction
   * gate (snippets/perf-defer-script.liquid) is on the page, its site:interacted event, or its
   * "interacted" class when the gate saw an interaction before this gallery started. Events sent by
   * code are ignored. The timer never starts a video while the tab is hidden: it then waits until the
   * tab shows. The start takes the same path as a first tap, so the block's own autoplay delay still
   * applies after it. The listeners and the timer all go at the first start, or when the gallery
   * leaves the page.
   *
   * @param {number} seconds - Seconds from navigation start, above 0.
   */
  #autoplayAfterInteractionOrTimer(seconds) {
    const inputEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];
    const signal = this.#controller.signal;
    const listenerOptions = { capture: true, passive: true, signal };
    let timer = 0;
    let done = false;

    const stop = () => {
      done = true;
      window.clearTimeout(timer);
      for (const type of inputEvents) window.removeEventListener(type, onInput, listenerOptions);
      document.removeEventListener('site:interacted', start, listenerOptions);
      document.removeEventListener('visibilitychange', onVisible, listenerOptions);
    };
    const start = () => {
      if (done) return;
      stop();
      this.#firstTapListening = false;
      this.#firstTapSeen = true;
      this.#syncActiveSlideVideo();
      this.#deferGridVideoSync();
    };
    /** @param {Event} event */
    const onInput = (event) => {
      if (!event.isTrusted) return;
      if (event.type === 'scroll' && event.target !== document) return;
      start();
    };
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') start();
    };
    const onTimer = () => {
      if (document.visibilityState === 'hidden') {
        document.addEventListener('visibilitychange', onVisible, listenerOptions);
        return;
      }
      start();
    };

    for (const type of inputEvents) window.addEventListener(type, onInput, listenerOptions);
    document.addEventListener('site:interacted', start, listenerOptions);
    signal.addEventListener('abort', stop, { once: true });
    const interacted = document.documentElement.classList.contains('interacted');
    timer = window.setTimeout(onTimer, interacted ? 0 : Math.max(0, seconds * 1000 - performance.now()));
  }

  #getVideoAutoplayDelay() {
    if (this.dataset.videoAutoplayDelayEnabled === 'false') return 0;

    const seconds = Number.parseFloat(this.dataset.videoAutoplayDelaySeconds);
    return Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : 2500;
  }

  /**
   * Autoplay product videos in the desktop images grid when enabled.
   * Never runs for mobile (`.media-gallery__images-grid-mobile` carousel) — that path
   * was loading every video tile and focusing content, which advanced the carousel (TWS-1029).
   */
  #syncGridVideos() {
    if (!this.classList.contains('media-gallery--images-grid')) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    if (!this.#shouldAutoplayVideo()) return;

    const grid = this.querySelector('.media-gallery__images-grid-desktop');
    if (!grid) return;

    const selector =
      '.product-media-container--video, .product-media-container--external_video, .product-media-container--video-360';

    for (const container of grid.querySelectorAll(selector)) {
      this.#playVideosInContainer(container);
    }
  }

  /**
   * Grid video autoplay is heavy; defer until after gallery paints / idle.
   * Desktop only (mobile must not start off-screen carousel videos).
   * Wait for deferred-media upgrade so post-morph clone tiles can loadContent.
   */
  #deferGridVideoSync() {
    if (!this.classList.contains('media-gallery--images-grid')) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;

    const run = () => this.#syncGridVideos();
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 400 });
      } else {
        setTimeout(run, 0);
      }
      // Second pass: CE upgrade / layout after replaceWith clone of section HTML.
      setTimeout(run, 120);
    };

    if (window.customElements?.whenDefined) {
      customElements.whenDefined('deferred-media').then(schedule).catch(schedule);
    } else {
      schedule();
    }
  }

  /**
   * @param {Element} slide
   * @returns {boolean}
   */
  #isVideoSlide(slide) {
    return (
      slide.classList.contains('product-media-container--video') ||
      slide.classList.contains('product-media-container--external_video') ||
      slide.classList.contains('product-media-container--video-360')
    );
  }

  /**
   * Autoplay video in the active gallery slide (Shopify deferred-media, native video, or iframe embed).
   *
   * @param {Element} slide
   * @param {{ force?: boolean }} [options] - When force is true (mobile user swipe), bypass desktop-only gate.
   */
  #playMediaInSlide(slide, { force = false } = {}) {
    if (!this.#isVideoSlide(slide)) return;
    if (!force && !this.#shouldAutoplayVideo()) return;
    this.#playVideosInContainer(slide);
  }

  /**
   * Pause every video/model in the gallery (load-time mobile safeguard).
   */
  #pauseAllGalleryVideos() {
    const roots = [
      this.querySelector('.media-gallery__images-grid-mobile'),
      this.querySelector('.media-gallery__images-grid-desktop'),
      this.slideshow,
      this,
    ].filter(Boolean);

    const seen = new Set();
    for (const root of roots) {
      if (seen.has(root)) continue;
      seen.add(root);
      this.#pauseVideosInContainer(root);
    }
  }

  /**
   * Autoplay video in a gallery tile (Shopify deferred-media, native video, or iframe embed).
   * Programmatic loads skip focus so the browser does not scroll the carousel to the video tile.
   *
   * @param {Element} container
   */
  #playVideosInContainer(container) {
    const pendingTimer = this.#videoAutoplayTimers.get(container);
    if (pendingTimer) window.clearTimeout(pendingTimer);

    const timer = window.setTimeout(() => {
      this.#videoAutoplayTimers.delete(container);

      for (const el of container.querySelectorAll('deferred-media, product-model')) {
        if (!('showDeferredMedia' in el) || typeof el.showDeferredMedia !== 'function') continue;

        // Live media may exist without the attribute after Liquid inline inject / partial morph.
        const hasLiveMedia = Boolean(el.querySelector(':scope > video, :scope > iframe'));

        if (!el.getAttribute('data-media-loaded') && !hasLiveMedia) {
          // Pass false so loadContent does not focus() the media (avoids scroll-into-view jumps).
          el.showDeferredMedia(false);
        } else {
          if (hasLiveMedia && !el.getAttribute('data-media-loaded')) {
            el.setAttribute('data-media-loaded', 'true');
          }
          if ('playMedia' in el && typeof el.playMedia === 'function') {
            el.playMedia();
          }
        }
      }

      for (const video of container.querySelectorAll('video')) {
        const host = video.closest('deferred-media');
        // Skip only when still inside <template> (not live). Template children are not in tree.
        if (host && !host.getAttribute('data-media-loaded') && !host.querySelector(':scope > video')) {
          continue;
        }
        video.muted = true;
        video.loop = this.dataset.videoLoop === 'true';
        video.setAttribute('playsinline', '');
        video.play().catch(() => {});
      }

      for (const iframe of container.querySelectorAll('iframe')) {
        if (iframe.closest('deferred-media') && !iframe.closest('deferred-media')?.getAttribute('data-media-loaded')) {
          const host = iframe.closest('deferred-media');
          if (!(host instanceof Element) || !host.querySelector(':scope > iframe')) {
            continue;
          }
        }

        this.#playIframe(iframe);
      }
    }, this.#getVideoAutoplayDelay());

    this.#videoAutoplayTimers.set(container, timer);
  }

  /**
   * @param {Element} slide
   */
  #pauseMediaInSlide(slide) {
    this.#pauseVideosInContainer(slide);
  }

  /**
   * @param {Element} container
   */
  #pauseVideosInContainer(container) {
    const pendingTimer = this.#videoAutoplayTimers.get(container);
    if (pendingTimer) {
      window.clearTimeout(pendingTimer);
      this.#videoAutoplayTimers.delete(container);
    }

    for (const el of container.querySelectorAll('deferred-media, product-model')) {
      if ('pauseMedia' in el && typeof el.pauseMedia === 'function') {
        el.pauseMedia();
      }
    }

    for (const video of container.querySelectorAll('video')) {
      video.pause();
    }

    for (const iframe of container.querySelectorAll('iframe')) {
      if (iframe.closest('deferred-media')) continue;

      this.#pauseIframe(iframe);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens the zoom dialog at the currently visible slide index (for mobile expand button).
   * Ensures the zoomed view shows the same image the user is viewing instead of always the first.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  openZoomAtCurrentSlide(event) {
    const slideshow = this.refs.slideshow;
    const index = slideshow?.current ?? 0;
    const slide = slideshow?.slides?.[index];
    if (
      slide?.classList.contains('product-media-container--video') ||
      slide?.classList.contains('product-media-container--external_video') ||
      slide?.classList.contains('product-media-container--video-360')
    ) {
      return;
    }
    this.zoom(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}